import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Role, rolesManipulator } from 'auth';
import { FormikErrors, FormikValues } from 'formik';
import omit from 'lodash/omit';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  GetInvestorEditByIdFullResponse,
  GetInvestorEditListFullResponse,
  InvestorEditsExtendFull,
} from '@/modules/entity/types/InvestorEditByIdResponse.ts';
import { CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { ISSUER_ENTITIES_QUERY_KEY } from '@/modules/issuer-dashboard/queries/entities.ts';
import { TOP_HOLDERS_QUERY_KEY } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import {
  CreateHolderDetailsRequest,
  GetInvestorEditListResponse,
  GetInvestorEditListV1InvestorEditGetParams,
  GetTaxReportIncomeResponse,
  GetTaxReportIncomeV1SrTaxReportDividendIncomeGetParams,
  HandleInvestorEditsRequest,
  HandleInvestorEditsResponse,
  UpdateHolderDetailsRequest,
} from 'api';

export const INVESTOR_EDIT_LIST_QUERY_KEY = 'vestra/investor-edit-list';
export const INVESTOR_EDIT_BY_ID_QUERY_KEY = 'vestra/investor-edit-by-id';
export const TAX_REPORT_INCOME_HOLDER_KEY = 'vestra/tax-report-income-holder';

export const useIssuerGetInvestorsEditListQuery = (
  payload: GetInvestorEditListV1InvestorEditGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();
  return useInfiniteQuery<GetInvestorEditListFullResponse>({
    queryKey: [
      INVESTOR_EDIT_LIST_QUERY_KEY,
      JSON.stringify(omit(payload, 'page')),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getInvestorEditListV1InvestorEditGet({
        ...payload,
        page: pageParam || payload.page,
      });

      const data: InvestorEditsExtendFull[] = response?.data?.data?.map(item => ({
        ...item,
        new_data: {
          ...item.new_data,
          entity_data: {
            type:
              (item as any)?.new_data?.entity?.type ||
              (item as any)?.new_data?.entity_data?.type ||
              (item as any)?.old_data?.entity?.type,
            entity_name:
              (item as any)?.new_data?.entity?.entity_name ||
              (item as any)?.new_data?.entity_data?.entity_name ||
              (item as any)?.old_data?.entity?.entity_name,
            ...(item as any)?.new_data?.entity,
            ...(item as any)?.new_data?.entity_data,
            document_files: [
              ...((item as any)?.new_data?.document_files || []),
              ...((item as any)?.old_data?.document_files || []),
            ],
          },
        },
        old_data: {
          ...item?.old_data,
          entity_data: {
            ...(item as any)?.old_data?.entity,
            ...(item as any)?.old_data?.entity_data,
            document_files: [
              ...((item as any)?.new_data?.document_files || []),
              ...((item as any)?.old_data?.document_files || []),
            ],
          },
        },
      }));
      return { ...response.data, data } as GetInvestorEditListFullResponse;
    },
    enabled: !disabled,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerGetInvestorEditByIdQuery = (id: number, disabled = false) => {
  const [params] = useSearchParams();
  return useQuery<GetInvestorEditByIdFullResponse>({
    queryKey: [INVESTOR_EDIT_BY_ID_QUERY_KEY, id, params.get(REGISTER_ID_SEARCH_PARAM_KEY) || ''],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getInvestorEditRequestByIdV1InvestorEditIdGet({ id });

      if (
        Object.values((response.data as any)?.data?.new_data?.entity_data)?.length === 0 &&
        !!(response.data as any)?.data?.new_data?.shareholder_id
      ) {
        const result: GetInvestorEditByIdFullResponse = {
          data: {
            ...response.data.data,
            new_data: {
              ...response.data.data.old_data,
              ...response.data.data.new_data,
              dividend_reinvestment:
                (response.data as any)?.data?.dividend_reinvestment ||
                (response.data as any)?.data?.new_data?.dividend_reinvestment ||
                (response.data as any)?.data?.new_data?.entity_data?.dividend_reinvestment ||
                (response.data as any)?.data?.old_data?.dividend_reinvestment,
              entity_data: {
                ...(response.data as any)?.data?.old_data,
                ...(response.data as any)?.data?.old_data?.entity,
                ...(response.data as any)?.data?.new_data?.entity,
                ...(response.data as any)?.data?.new_data?.entity_data,
                document_files: [
                  ...((response.data as any)?.data?.old_data?.document_files || []),
                  ...((response.data as any)?.data?.new_data?.document_files || []),
                ],
              },
              document_files: [
                ...((response.data as any)?.data?.old_data?.document_files || []),
                ...((response.data as any)?.data?.new_data?.document_files || []),
              ],
            },
          },
        };

        return result as GetInvestorEditByIdFullResponse;
      }

      const result: GetInvestorEditByIdFullResponse = {
        data: {
          ...response.data.data,
          new_data: {
            ...response.data.data.new_data,
            dividend_reinvestment:
              (response.data as any)?.data?.dividend_reinvestment ||
              (response.data as any)?.data?.new_data?.dividend_reinvestment ||
              (response.data as any)?.data?.new_data?.entity_data?.dividend_reinvestment ||
              (response.data as any)?.data?.old_data?.dividend_reinvestment,
            entity_data: {
              ...(response.data as any)?.data?.new_data?.entity,
              ...(response.data as any)?.data?.new_data?.entity_data,
              document_files: [
                ...((response.data as any)?.data?.new_data?.document_files || []),
                ...((response.data as any)?.data?.old_data?.document_files || []),
              ],
            },
          },
          old_data: {
            ...response.data.data.old_data,
            entity_data: {
              ...(response.data as any)?.data?.old_data?.entity,
              ...(response.data as any)?.data?.old_data?.entity_data,
              document_files: [
                ...((response.data as any)?.data?.new_data?.document_files || []),
                ...((response.data as any)?.data?.old_data?.document_files || []),
              ],
            },
          },
        },
      };

      return result as GetInvestorEditByIdFullResponse;
    },
    enabled: !disabled,
  });
};

export const useCreateInvestorEditMutation = ({
  onSuccess,
}: {
  onSuccess?: (data: GetInvestorEditByIdFullResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateHolderDetailsRequest) => {
      const response = await withErrorHandling(
        api.v1.createInvestorEditRequestV1InvestorEditPost(payload),
        {
          loading: 'Creating editing request...',
          success: 'Editing request created',
        },
      );
      const result: GetInvestorEditByIdFullResponse = {
        data: {
          ...response.data.data,
          new_data: {
            ...response.data.data.new_data,
            entity_data: {
              ...(response.data as any).data.new_data?.entity_data,
              ...(response.data as any).data.new_data?.entity,
            },
          },
        },
      };
      return result as GetInvestorEditByIdFullResponse;
    },
    onSuccess: (data: GetInvestorEditByIdFullResponse) => {
      queryClient.invalidateQueries([INVESTOR_EDIT_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([INVESTOR_EDIT_LIST_QUERY_KEY]);
      queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TOP_HOLDERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY] });
      onSuccess && onSuccess(data);
    },
  });
};

export const useUpdateInvestorEditMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: GetInvestorEditByIdFullResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateHolderDetailsRequest) => {
      const response = await withErrorHandling(
        api.v1.updateInvestorEditRequestV1InvestorEditPatch(payload),
        {
          loading: 'Pending change updating...',
          success:
            'Pending changes updated. Not applied yet, go to Confirmation step and click Submit to submit it for review to apply change.',
          setErrors,
          notificationDuration: 5000,
        },
      );
      return response.data as GetInvestorEditByIdFullResponse;
    },
    onSuccess: async (data: GetInvestorEditByIdFullResponse) => {
      await queryClient.invalidateQueries([INVESTOR_EDIT_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([INVESTOR_EDIT_LIST_QUERY_KEY]);
      queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TOP_HOLDERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY] });
      if (onSuccess) onSuccess(data);
    },
  });
};

export const useInvestorEditChangeStatusToAwaitingApprovalMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: GetInvestorEditListResponse) => void;
} = {}) => {
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.changeStatusToAwaitingApprovalV1InvestorEditAwaitingApprovalIdPatch({ id }),
        {
          loading: isIssuerAdmin
            ? 'Entity change approving...'
            : 'Changing entity status to awaiting approval...',
          success: isIssuerAdmin
            ? 'Entity change approved'
            : 'Changed entity status to awaiting approval',
          setErrors,
        },
      );
      return response.data as GetInvestorEditListResponse;
    },
    onSuccess: (data: GetInvestorEditListResponse) => {
      if (onSuccess) onSuccess(data);
      queryClient.invalidateQueries([INVESTOR_EDIT_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([INVESTOR_EDIT_LIST_QUERY_KEY]);
      queryClient.invalidateQueries({
        queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TOP_HOLDERS_QUERY_KEY] });
    },
  });
};

export const useInvestorEditUpdateStatusMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: HandleInvestorEditsResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: HandleInvestorEditsRequest) => {
      const response = await withErrorHandling(
        api.v1.updateInvestorEditStatusV1InvestorEditStatusPatch(payload),
        {
          loading: 'Updating entity status...',
          success: 'Updated entity status',
          setErrors,
        },
      );
      return response.data as HandleInvestorEditsResponse;
    },
    onSuccess: (data: HandleInvestorEditsResponse) => {
      if (onSuccess) onSuccess(data);
      queryClient.invalidateQueries([INVESTOR_EDIT_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([INVESTOR_EDIT_LIST_QUERY_KEY]);
      queryClient.invalidateQueries({
        queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TOP_HOLDERS_QUERY_KEY] });
    },
  });
};

export const useGetTaxReportIncomeHolderQuery = (
  payload: GetTaxReportIncomeV1SrTaxReportDividendIncomeGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  return useQuery<GetTaxReportIncomeResponse>({
    queryKey: [TAX_REPORT_INCOME_HOLDER_KEY, registerId, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTaxReportIncomeV1SrTaxReportDividendIncomeGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};
